import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { fetchAdminRoles, queryKeys, type AdminRoleRow } from '../lib/api';
import { useAdminAuth } from '../lib/auth-context';
import { PERMS } from '../../rbac/permissions';

export function RolesListPage() {
  const navigate = useNavigate();
  const { hasPerm } = useAdminAuth();
  const canManage = hasPerm(PERMS.ROLES_MANAGE);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.adminRoles,
    queryFn: fetchAdminRoles,
  });

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-heading-2 text-primary">Roles</h1>
          <p className="text-body-sm text-secondary">Admin roles and their permission grants.</p>
        </div>
        {canManage && (
          <Button onClick={() => void navigate({ to: '/admin-roles/new' })}>Create role</Button>
        )}
      </header>

      {isLoading ? (
        <div className="overflow-x-auto">
          <Table>
            <TableBody>
              <SkeletonTable rows={6} cols={5} />
            </TableBody>
          </Table>
        </div>
      ) : isError ? (
        <p className="text-body-sm text-danger-text">Could not load roles. Retry in a moment.</p>
      ) : !data || data.length === 0 ? (
        <EmptyState title="No roles" description="Create a role to grant admin permissions." />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Description</TableHeaderCell>
                <TableHeaderCell className="text-right">Permissions</TableHeaderCell>
                <TableHeaderCell className="text-right">Operators</TableHeaderCell>
                <TableHeaderCell>Origin</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r: AdminRoleRow) => (
                <TableRow key={r.roleId}>
                  <TableCell>
                    <Link to="/admin-roles/$roleId" params={{ roleId: r.roleId }}>
                      {r.name}
                    </Link>
                  </TableCell>
                  <TableCell>{r.description ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.permissions.length}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.assigneeCount}</TableCell>
                  <TableCell>
                    {r.isBuiltin ? (
                      <Badge variant="neutral">Built-in</Badge>
                    ) : (
                      <span className="text-muted">Custom</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
